import React, { useState, useEffect } from 'react'
import {
  ArrowRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  Bars3Icon,
  InformationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'

/**
 * Panel de configuración de flujo para templates conversacionales
 * Permite ver y editar expectedFlow y formFlow
 */
const FlowConfigPanel = ({ elements, campaignName, onUpdateElements, expectedFlow: externalExpectedFlow, formFlow: externalFormFlow }) => {
  const [expectedFlow, setExpectedFlow] = useState({})
  const [formFlow, setFormFlow] = useState([])
  const [isExpanded, setIsExpanded] = useState(false)
  const [editingValidation, setEditingValidation] = useState(null)
  const [availableElements, setAvailableElements] = useState([])

  useEffect(() => {
    if (elements) {
      const parsed = typeof elements === 'string' ? JSON.parse(elements) : elements
      // Use external expectedFlow/formFlow if provided (from fullTemplate), otherwise fall back to elements
      if (externalExpectedFlow) {
        setExpectedFlow(
          typeof externalExpectedFlow === 'string'
            ? JSON.parse(externalExpectedFlow)
            : externalExpectedFlow
        )
      } else if (parsed.expectedFlow) {
        setExpectedFlow(
          typeof parsed.expectedFlow === 'string'
            ? JSON.parse(parsed.expectedFlow)
            : parsed.expectedFlow
        )
      }
      if (externalFormFlow) {
        setFormFlow(Array.isArray(externalFormFlow) ? externalFormFlow : [])
      } else if (parsed.formFlow) {
        setFormFlow(Array.isArray(parsed.formFlow) ? parsed.formFlow : [])
      }
      // Build list of available elements
      const elems = Object.keys(parsed)
        .filter(
          (key) =>
            key.startsWith(campaignName) &&
            !key.includes('initSMS') &&
            key !== 'expectedFlow' &&
            key !== 'formFlow' &&
            key !== 'type' &&
            key !== 'rbmFlowAliasesMap'
        )
        .map((key) => ({
          key,
          display: key.replace(`${campaignName}-`, '')
        }))
      setAvailableElements(elems)
    }
  }, [elements, campaignName, externalExpectedFlow, externalFormFlow])

  const getElementDisplayName = (elementKey) => {
    if (!elementKey) return 'N/A'
    return elementKey.replace(`${campaignName}-`, '')
  }

  const handleSaveValidation = (elementKey, config) => {
    const newExpectedFlow = { ...expectedFlow }
    if (config) {
      newExpectedFlow[elementKey] = config
    } else {
      delete newExpectedFlow[elementKey]
    }
    setExpectedFlow(newExpectedFlow)

    // Update parent
    if (onUpdateElements) {
      const parsed = typeof elements === 'string' ? JSON.parse(elements) : elements
      parsed.expectedFlow = newExpectedFlow
      onUpdateElements(parsed)
    }
    setEditingValidation(null)
  }

  const handleToggleFormFlow = (elementKey) => {
    const cleanKey = elementKey.startsWith(campaignName)
      ? elementKey
      : `${campaignName}-${elementKey}`
    const isInFormFlow = formFlow.includes(cleanKey)
    let newFormFlow
    if (isInFormFlow) {
      newFormFlow = formFlow.filter((k) => k !== cleanKey)
    } else {
      newFormFlow = [...formFlow, cleanKey]
    }
    setFormFlow(newFormFlow)

    if (onUpdateElements) {
      const parsed = typeof elements === 'string' ? JSON.parse(elements) : elements
      parsed.formFlow = newFormFlow
      onUpdateElements(parsed)
    }
  }

  const moveFormFlowItem = (index, direction) => {
    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === formFlow.length - 1) return
    const newFormFlow = [...formFlow]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    ;[newFormFlow[index], newFormFlow[targetIndex]] = [
      newFormFlow[targetIndex],
      newFormFlow[index]
    ]
    setFormFlow(newFormFlow)
    if (onUpdateElements) {
      const parsed = typeof elements === 'string' ? JSON.parse(elements) : elements
      parsed.formFlow = newFormFlow
      onUpdateElements(parsed)
    }
  }

  if (!isExpanded) {
    return (
      <div className="mb-4">
        <button
          onClick={() => setIsExpanded(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-700 rounded-xl hover:bg-indigo-100 transition-colors text-sm font-medium border border-indigo-100"
        >
          <ArrowRightIcon className="h-4 w-4" />
          Configuración de Flujo (expectedFlow / formFlow)
          {Object.keys(expectedFlow).length > 0 && (
            <span className="bg-indigo-200 text-indigo-800 text-xs px-2 py-0.5 rounded-full font-semibold">
              {Object.keys(expectedFlow).length} validaciones
            </span>
          )}
          {formFlow.length > 0 && (
            <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-semibold">
              {formFlow.length} en secuencia
            </span>
          )}
        </button>
      </div>
    )
  }

  return (
    <div className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <CheckCircleIcon className="h-5 w-5 text-indigo-500" />
          Configuración de Flujo Conversacional
        </h3>
        <button
          onClick={() => setIsExpanded(false)}
          className="text-gray-400 hover:text-gray-600 text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Ocultar
        </button>
      </div>

      <div className="p-5 space-y-8">
        {/* formFlow Section */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Bars3Icon className="h-4 w-4 text-indigo-500" />
            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Secuencia de Preguntas (formFlow)
            </h4>
          </div>
          <p className="text-xs text-gray-400 mb-3">
            Marca los elementos que requieren respuesta del usuario. Puedes reordenarlos con las
            flechas:
          </p>

          {/* Selected formFlow items - ordered list */}
          {formFlow.length > 0 && (
            <div className="mb-3 space-y-1.5">
              {formFlow.map((key, index) => {
                const display = getElementDisplayName(key)
                return (
                  <div
                    key={key}
                    className="flex items-center gap-2 p-2.5 bg-indigo-50 border border-indigo-100 rounded-lg"
                  >
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-indigo-500 text-white text-xs font-bold rounded-full">
                      {index + 1}
                    </span>
                    <span className="text-sm font-medium text-gray-700 flex-1">{display}</span>
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => moveFormFlowItem(index, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-100 rounded disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                        title="Mover arriba"
                      >
                        <ArrowPathIcon className="h-3.5 w-3.5 rotate-180" />
                      </button>
                      <button
                        onClick={() => moveFormFlowItem(index, 'down')}
                        disabled={index === formFlow.length - 1}
                        className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-100 rounded disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                        title="Mover abajo"
                      >
                        <ArrowPathIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleToggleFormFlow(key)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors ml-1"
                        title="Quitar de la secuencia"
                      >
                        <XCircleIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Available elements to add */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-500">Elementos disponibles</span>
            </div>
            <div className="p-2 space-y-0.5 max-h-48 overflow-y-auto">
              {availableElements.map((elem) => {
                const isInFormFlow = formFlow.includes(elem.key)
                if (isInFormFlow) return null
                return (
                  <label
                    key={elem.key}
                    className="flex items-center gap-2.5 p-2 hover:bg-gray-50 rounded-md cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={() => handleToggleFormFlow(elem.key)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                    />
                    <span className="text-sm text-gray-700">{elem.display}</span>
                  </label>
                )
              })}
              {availableElements.filter((e) => !formFlow.includes(e.key)).length === 0 && (
                <p className="text-sm text-gray-400 italic p-2 text-center">
                  Todos los elementos están en la secuencia
                </p>
              )}
            </div>
          </div>

          {formFlow.length === 0 && (
            <p className="text-sm text-gray-400 italic mt-3 text-center py-3 bg-gray-50 rounded-lg border border-dashed border-gray-200">
              No hay secuencia configurada. Marca elementos arriba para añadirlos.
            </p>
          )}
        </div>

        {/* expectedFlow Section */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircleIcon className="h-4 w-4 text-green-500" />
            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Validaciones de Respuesta (expectedFlow)
            </h4>
          </div>

          {/* Add new validation button */}
          <div className="mb-3">
            <select
              onChange={(e) => {
                if (e.target.value) {
                  setEditingValidation({
                    elementKey: e.target.value,
                    isNew: true,
                    config: {
                      expectedAnswers: [''],
                      elementOk: '',
                      elementWrong: '',
                      flowOk: [],
                      flowWrong: []
                    }
                  })
                }
              }}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              value=""
            >
              <option value="">+ Añadir validación para elemento...</option>
              {availableElements
                .filter((elem) => !expectedFlow[elem.key])
                .map((elem) => (
                  <option key={elem.key} value={elem.key}>
                    {elem.display}
                  </option>
                ))}
            </select>
          </div>

          {/* Existing validations */}
          {Object.keys(expectedFlow).length === 0 && !editingValidation && (
            <p className="text-sm text-gray-400 italic text-center py-3 bg-gray-50 rounded-lg border border-dashed border-gray-200">
              No hay validaciones configuradas
            </p>
          )}

          <div className="space-y-2">
            {Object.entries(expectedFlow).map(([elementKey, config]) => (
              <div
                key={elementKey}
                className="border border-gray-200 rounded-xl overflow-hidden bg-white"
              >
                {editingValidation?.elementKey === elementKey && !editingValidation?.isNew ? (
                  /* Editing mode */
                  <div className="p-4">
                    <ValidationEditor
                      elementKey={elementKey}
                      config={config}
                      availableElements={availableElements}
                      onSave={(newConfig) => handleSaveValidation(elementKey, newConfig)}
                      onCancel={() => setEditingValidation(null)}
                      onDelete={() => handleSaveValidation(elementKey, null)}
                    />
                  </div>
                ) : (
                  /* View mode */
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                        <span className="text-sm font-semibold text-gray-800">
                          {getElementDisplayName(elementKey)}
                        </span>
                      </div>
                      <button
                        onClick={() =>
                          setEditingValidation({ elementKey, config, isNew: false })
                        }
                        className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-indigo-50 transition-colors font-medium"
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                        Editar
                      </button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="text-gray-400 font-medium text-xs uppercase tracking-wide w-20 flex-shrink-0 pt-0.5">
                          Espera:
                        </span>
                        <span className="font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                          {config.expectedAnswers?.join(', ') || 'Cualquier respuesta'}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-green-600 font-medium text-xs uppercase tracking-wide w-20 flex-shrink-0 pt-0.5">
                          ✓ Correcto
                        </span>
                        <span className="text-green-700 font-medium">
                          → {getElementDisplayName(config.elementOk)}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-red-500 font-medium text-xs uppercase tracking-wide w-20 flex-shrink-0 pt-0.5">
                          ✗ Incorrecto
                        </span>
                        <span className="text-red-600 font-medium">
                          → {getElementDisplayName(config.elementWrong)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* New validation editor */}
          {editingValidation?.isNew && (
            <div className="border-2 border-indigo-200 rounded-xl p-4 bg-indigo-50/50 mt-2">
              <ValidationEditor
                elementKey={editingValidation.elementKey}
                config={editingValidation.config}
                availableElements={availableElements}
                onSave={(newConfig) =>
                  handleSaveValidation(editingValidation.elementKey, newConfig)
                }
                onCancel={() => setEditingValidation(null)}
              />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="text-xs text-gray-500 bg-gray-50 p-4 rounded-xl border border-gray-200">
          <div className="flex items-center gap-1.5 mb-2">
            <InformationCircleIcon className="h-4 w-4 text-gray-400" />
            <p className="font-semibold text-gray-600">¿Qué es esto?</p>
          </div>
          <div className="space-y-1 ml-5.5">
            <p>
              • <strong>formFlow:</strong> Secuencia de preguntas que el usuario debe responder en
              orden.
            </p>
            <p>
              • <strong>expectedFlow:</strong> Validaciones de respuestas. Si el usuario responde
              correctamente, va a &quot;elementOk&quot;. Si no, va a &quot;elementWrong&quot;.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Editor de validación individual
 */
const ValidationEditor = ({ elementKey, config, availableElements, onSave, onCancel, onDelete }) => {
  const [localConfig, setLocalConfig] = useState({
    expectedAnswers: config?.expectedAnswers?.length ? config.expectedAnswers : [''],
    elementOk: config?.elementOk || '',
    elementWrong: config?.elementWrong || '',
    flowOk: config?.flowOk || [],
    flowWrong: config?.flowWrong || []
  })

  const updateAnswer = (index, value) => {
    const newAnswers = [...localConfig.expectedAnswers]
    newAnswers[index] = value
    setLocalConfig({ ...localConfig, expectedAnswers: newAnswers })
  }

  const addAnswer = () => {
    setLocalConfig({
      ...localConfig,
      expectedAnswers: [...localConfig.expectedAnswers, '']
    })
  }

  const removeAnswer = (index) => {
    const newAnswers = localConfig.expectedAnswers.filter((_, i) => i !== index)
    setLocalConfig({ ...localConfig, expectedAnswers: newAnswers })
  }

  const getCleanElementKey = (key) => {
    return key?.replace(/^.*?-/, '') || key
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
          <span className="text-sm font-semibold text-gray-800">
            {getCleanElementKey(elementKey)}
          </span>
        </div>
        {onDelete && (
          <button
            onClick={onDelete}
            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-red-50 transition-colors"
          >
            <TrashIcon className="h-3.5 w-3.5" />
            Eliminar
          </button>
        )}
      </div>

      {/* Respuestas esperadas */}
      <div className="bg-white rounded-lg p-3 border border-gray-200">
        <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
          Respuestas esperadas
        </label>
        <p className="text-xs text-gray-400 mb-3">
          Usa {'{variable}'} para variables de contacto. Usa .* para cualquier respuesta.
        </p>
        <div className="space-y-2">
          {localConfig.expectedAnswers.map((answer, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={answer}
                onChange={(e) => updateAnswer(index, e.target.value)}
                placeholder="Ej: {dniLetter} o .*"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              {localConfig.expectedAnswers.length > 1 && (
                <button
                  onClick={() => removeAnswer(index)}
                  className="px-2.5 py-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors text-sm"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          onClick={addAnswer}
          className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-indigo-50 transition-colors font-medium"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Añadir respuesta
        </button>
      </div>

      {/* Destino correcto */}
      <div className="bg-white rounded-lg p-3 border border-green-200">
        <label className="block text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">
          Si acierta → ir a:
        </label>
        <select
          value={localConfig.elementOk}
          onChange={(e) => setLocalConfig({ ...localConfig, elementOk: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
        >
          <option value="">-- Selecciona elemento --</option>
          {availableElements.map((elem) => (
            <option key={elem.key} value={elem.key}>
              {elem.display}
            </option>
          ))}
        </select>
      </div>

      {/* Destino incorrecto */}
      <div className="bg-white rounded-lg p-3 border border-red-200">
        <label className="block text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">
          Si falla → ir a:
        </label>
        <select
          value={localConfig.elementWrong}
          onChange={(e) => setLocalConfig({ ...localConfig, elementWrong: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
        >
          <option value="">-- Selecciona elemento --</option>
          {availableElements.map((elem) => (
            <option key={elem.key} value={elem.key}>
              {elem.display}
            </option>
          ))}
        </select>
      </div>

      {/* Buttons */}
      <div className="flex gap-2 pt-2">
        <button
          onClick={() => onSave(localConfig)}
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
        >
          Guardar
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
      </div>
    </div>
  )
}

export default FlowConfigPanel
